/**
 * Instant print-on-demand order intake for smaller jobs — ported from
 * frontend/src/pages/PrintOrderIntake.js, restyled to noosa-frontend's shared "new style" system.
 * Assumes Noosa supplies the blank garment (its pricing has no garment/print split) — a
 * customer-supplied-garment job always goes through DesignOrderPage's quote path instead,
 * regardless of quantity. See docs/NOOSA-ORDER-INTAKE-TASKS.md Tasks 7/8/11.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useFileUpload, garmentBreaks, garmentUnitPrice, PrintJobFields, emptyPrintJob, sizesTotal, isRushDate, RUSH_FEE_PERCENT } from 'shared-ui';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OptionCard from '../components/OptionCard';
import SuccessState from '../components/SuccessState';
import { usePortal } from '../context/PortalContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

function priceForQuantity(tiers, quantity) {
  const sorted = [...tiers].sort((a, b) => a.minQty - b.minQty);
  let unitPrice = sorted[0]?.price ?? 0;
  for (const tier of sorted) {
    if (quantity >= tier.minQty) unitPrice = tier.price;
  }
  return unitPrice;
}

export default function PrintOrderIntake() {
  const { portal, products = [] } = usePortal();
  const businessId = portal?.business?.id;
  const { customer, isAuthenticated } = useCustomerAuth();
  const { upload, uploading, error: uploadError } = useFileUpload({
    apiBaseUrl: API_BASE_URL,
    tokenKey: 'customer_token',
  });

  const [searchParams] = useSearchParams();
  const initialQuantity = Math.max(1, Number(searchParams.get('quantity')) || 1);
  // Which garment (2026-09-28): priced from its own price and quantity breaks, same as the
  // Products page - "?product=<id>" preselects one. Blank = the general print price list.
  const [productId, setProductId] = useState(searchParams.get('product') || '');

  const [tiers, setTiers] = useState([]);
  const [pricingError, setPricingError] = useState('');
  // Sizes, colour, placement, needed-by and artwork (2026-09-28) - the quantity is the sizes' total.
  const [job, setJob] = useState(() => ({ ...emptyPrintJob(), sizes: { M: initialQuantity } }));
  const quantity = Math.max(1, sizesTotal(job.sizes));
  // "Order again" from the customer's order page: /print-order?repeat=<orderId>&t=<token>
  const repeatId = searchParams.get('repeat');
  const repeatToken = searchParams.get('t');
  const [repeatOf, setRepeatOf] = useState(null);
  useEffect(() => {
    if (!repeatId || !repeatToken) return;
    fetch(`${API_BASE_URL}/print-orders/track/${repeatId}?t=${encodeURIComponent(repeatToken)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const o = d?.order;
        if (!o) return;
        const spec = o.printSpec || {};
        setRepeatOf(o);
        setJob((j) => ({
          ...j,
          sizes: spec.sizes || { M: o.items?.[0]?.quantity || 1 },
          colour: spec.colour || '',
          placements: spec.placements || j.placements,
          printColours: spec.printColours || j.printColours,
        }));
        const pid = o.items?.find((i) => i.productId)?.productId;
        if (pid) setProductId(pid);
        if (o.customerNotes) setNotes(o.customerNotes);
        if (o.deliveryMode) setDeliveryMode(o.deliveryMode);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatId, repeatToken]);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('pickup');
  const [notes, setNotes] = useState('');
  const [designFile, setDesignFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [payingNow, setPayingNow] = useState(false);
  const [payError, setPayError] = useState('');

  useEffect(() => {
    if (!businessId) return;
    fetch(API_ENDPOINTS.printOrders.pricing(businessId))
      .then((res) => {
        if (!res.ok) throw new Error('Pricing is not available right now');
        return res.json();
      })
      .then((data) => setTiers(data.pricing?.tiers || []))
      .catch((err) => setPricingError(err.message || 'Pricing is not available right now'));
  }, [businessId]);

  useEffect(() => {
    if (isAuthenticated && customer) {
      setCustomerName(`${customer.firstName || ''} ${customer.lastName || ''}`.trim());
      setCustomerEmail(customer.email || '');
      setCustomerPhone(customer.phone || '');
    }
  }, [isAuthenticated, customer]);

  // Start on the first garment once products have loaded, unless one was preselected.
  const [autoPicked, setAutoPicked] = useState(false);
  useEffect(() => {
    if (autoPicked || products.length === 0) return;
    setAutoPicked(true);
    setProductId((current) => current || products[0].id);
  }, [products, autoPicked]);

  const garment = products.find((p) => p.id === productId) || null;
  const breaks = useMemo(
    () => (garment ? garmentBreaks(garment) : [...tiers].sort((a, b) => a.minQty - b.minQty)),
    [garment, tiers]
  );
  const unitPrice = useMemo(
    () => (garment ? garmentUnitPrice(garment, quantity) || 0 : tiers.length > 0 ? priceForQuantity(tiers, quantity) : 0),
    [garment, tiers, quantity]
  );
  const itemsTotal = Math.round(unitPrice * quantity * 100) / 100;
  const rushFee = isRushDate(job.neededBy) ? Math.round(itemsTotal * (RUSH_FEE_PERCENT / 100) * 100) / 100 : 0;
  const total = Math.round((itemsTotal + rushFee) * 100) / 100;

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const confirmed = await upload(file, { fileType: 'DESIGN_ASSET' });
        setDesignFile(confirmed);
      } catch {
        // upload() already sets its own `error` state, surfaced below
      }
    },
    [upload]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!customerName.trim() || !customerEmail.trim()) {
      setSubmitError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.printOrders.intake, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId,
          quantity,
          customerName,
          customerEmail,
          customerPhone: customerPhone || undefined,
          deliveryMode,
          notes: notes || undefined,
          designFileIds: [...(designFile ? [designFile.id] : []), ...job.files.map((f) => f.id)].length > 0
            ? [...(designFile ? [designFile.id] : []), ...job.files.map((f) => f.id)]
            : undefined,
          productId: productId || undefined,
          sizes: job.sizes,
          colour: job.colour || undefined,
          placements: job.placements,
          printColours: job.printColours,
          neededBy: job.neededBy || undefined,
          repeatOfOrderId: repeatOf?.id || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not submit your order');
      setConfirmedOrder(data.order);
    } catch (err) {
      setSubmitError(err.message || 'Could not submit your order');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = async () => {
    if (!confirmedOrder) return;
    setPayError('');
    setPayingNow(true);
    try {
      const res = await fetch(API_ENDPOINTS.printOrders.checkout(confirmedOrder.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/orders/${confirmedOrder.id}?paid=1`,
          cancelUrl: `${window.location.origin}/orders/${confirmedOrder.id}`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start checkout');
      window.location.href = data.url;
    } catch (err) {
      setPayError(err.message || 'Could not start checkout');
      setPayingNow(false);
    }
  };

  return (
    <>
      <Header />
      <main className="section">
        <div className="container" style={{ maxWidth: 560 }}>
          {confirmedOrder ? (
            <SuccessState
              title="Order received"
              message={`Order ${confirmedOrder.orderNumber} — $${confirmedOrder.total?.toFixed?.(2) ?? confirmedOrder.total}. Pay now to lock in your spot in production, or pay later from your account.`}
              steps={['Order placed', 'Artwork checked', 'Proof to approve', 'Printing', deliveryMode === 'pickup' ? 'Ready for pickup' : 'Shipped']}
            >
              <button
                type="button"
                onClick={handlePayNow}
                disabled={payingNow}
                className="btn btn-primary"
                style={{ marginTop: '8px', width: '100%' }}
              >
                {payingNow ? 'Redirecting to secure checkout…' : `Pay $${confirmedOrder.total?.toFixed?.(2) ?? confirmedOrder.total} now`}
              </button>
              {payError && <div style={{ ...errorStyle, marginTop: '10px' }}>{payError}</div>}
              {confirmedOrder.customerAccessToken ? (
                <Link to={`/order/${confirmedOrder.id}?t=${confirmedOrder.customerAccessToken}`} className="btn btn-secondary" style={{ marginTop: '8px' }}>
                  Track this order
                </Link>
              ) : isAuthenticated ? (
                <a href="/account/orders" className="btn btn-secondary" style={{ marginTop: '8px' }}>
                  Track this order
                </a>
              ) : null}
            </SuccessState>
          ) : (
            <>
              <div className="masthead">
                <span className="eyebrow">Print on demand</span>
                <h1>Start a custom print order</h1>
                <p>Tell us what you need — price drops automatically as the quantity goes up.</p>
              </div>

              {pricingError && products.length === 0 && <div style={errorStyle}>{pricingError}</div>}

              <form onSubmit={handleSubmit}>
                {products.length > 0 && (
                  <>
                    <label style={labelStyle}>Garment</label>
                    <select value={productId} onChange={(e) => setProductId(e.target.value)} style={inputStyle}>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} - from ${Math.min(...garmentBreaks(p).map((b) => b.price)).toFixed(2)}
                        </option>
                      ))}
                      {tiers.length > 0 && <option value="">Something else - we'll help you choose</option>}
                    </select>
                  </>
                )}

                {repeatOf && (
                  <div style={{ padding: '10px 12px', borderRadius: '8px', background: 'var(--light-bg, #f5f5f5)', fontSize: '13px', marginBottom: '16px' }}>
                    🔁 Starting from order {repeatOf.orderNumber} - same artwork comes across. Change anything you like.
                  </div>
                )}

                <PrintJobFields apiBaseUrl={API_BASE_URL} value={job} onChange={setJob} />

                {breaks.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--text-light, #666)', marginBottom: '8px' }}>
                    Price breaks: {breaks.map((t) => `${t.minQty}+ $${t.price}/ea`).join(' · ')}
                  </div>
                )}

                <div style={{ marginBottom: '20px', fontSize: '15px' }}>
                  Estimated total: <strong>${total.toFixed(2)}</strong> ({quantity} × {garment ? garment.name : 'custom print'} at ${unitPrice.toFixed(2)}/ea
                  {rushFee > 0 ? ` + ⚡ rush $${rushFee.toFixed(2)}` : ''})
                </div>

                <label style={labelStyle}>Your name</label>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required style={inputStyle} />

                <label style={labelStyle}>Email</label>
                <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required style={inputStyle} />

                <label style={labelStyle}>Phone (optional)</label>
                <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} style={inputStyle} />

                <label style={labelStyle}>Pickup or ship?</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                  <OptionCard title="🏬 Pickup" selected={deliveryMode === 'pickup'} onClick={() => setDeliveryMode('pickup')} />
                  <OptionCard title="📦 Ship" selected={deliveryMode === 'ship'} onClick={() => setDeliveryMode('ship')} />
                </div>

                {/* Images upload above for everyone (PrintJobFields). Logged-in customers can also send
                    a vector file (PDF/AI/EPS) - that upload needs an account. */}
                {isAuthenticated && (
                  <>
                    <label style={labelStyle}>Vector file (optional - PDF, AI, EPS, SVG)</label>
                    <input type="file" accept=".pdf,.ai,.eps,.svg" onChange={handleFileChange} disabled={uploading} />
                    {uploading && <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>Uploading…</div>}
                    {uploadError && <div style={{ fontSize: '13px', color: '#b91c1c', marginTop: '4px' }}>{uploadError}</div>}
                    {designFile && (
                      <div style={{ fontSize: '13px', color: 'var(--primary-color)', marginTop: '4px' }}>✓ {designFile.filename} uploaded</div>
                    )}
                  </>
                )}

                <label style={{ ...labelStyle, marginTop: '16px' }}>Notes (design details, sizes, colors, deadline…)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />

                {submitError && <div style={errorStyle}>{submitError}</div>}

                <button type="submit" disabled={submitting || unitPrice <= 0} className="btn btn-primary" style={{ width: '100%', marginTop: '8px' }}>
                  {submitting ? 'Submitting…' : 'Submit order'}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' };
const inputStyle = { width: '100%', padding: '10px', marginBottom: '16px', border: '1px solid #ddd', borderRadius: '6px', fontFamily: 'inherit' };
const errorStyle = { background: '#fef2f2', color: '#ef4444', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' };
